import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface AdapterMappingPreviewSource {
  fileName: string;
  sourceCsv: string;
  revision: number;
}

export type AdapterMappingDraft = Record<string, unknown>;

interface AdapterMappingDraftPatch {
  values: AdapterMappingDraft;
  revision: number;
}

interface AdapterMappingEditorState {
  dirty: boolean;
  saving: boolean;
  save: () => Promise<boolean>;
}

interface AdapterMappingPreviewContextValue {
  draftsByMappingId: Record<string, AdapterMappingDraft>;
  patchesByMappingId: Record<string, AdapterMappingDraftPatch>;
  previewsByMappingId: Record<string, AdapterMappingPreviewSource>;
  editorsByMappingId: Record<string, AdapterMappingEditorState>;
  clearMappingDraft: (mappingId: string) => void;
  clearMappingPatch: (mappingId: string, revision: number) => void;
  patchMappingDraft: (mappingId: string, values: AdapterMappingDraft) => void;
  setMappingDraft: (mappingId: string, values: AdapterMappingDraft) => void;
  setMappingPreview: (mappingId: string, fileName: string, sourceCsv: string) => void;
  setMappingEditor: (mappingId: string, editor: AdapterMappingEditorState) => void;
  clearMappingEditor: (mappingId: string) => void;
}

const AdapterMappingPreviewContext = createContext<AdapterMappingPreviewContextValue | undefined>(undefined);

export const AdapterMappingPreviewProvider = ({ children }: PropsWithChildren) => {
  const [draftsByMappingId, setDraftsByMappingId] = useState<Record<string, AdapterMappingDraft>>({});
  const [patchesByMappingId, setPatchesByMappingId] = useState<Record<string, AdapterMappingDraftPatch>>({});
  const [previewsByMappingId, setPreviewsByMappingId] = useState<Record<string, AdapterMappingPreviewSource>>({});
  const [editorsByMappingId, setEditorsByMappingId] = useState<Record<string, AdapterMappingEditorState>>({});
  const setMappingEditor = useCallback((mappingId: string, editor: AdapterMappingEditorState) => {
    setEditorsByMappingId((current) => current[mappingId]?.dirty === editor.dirty &&
      current[mappingId]?.saving === editor.saving && current[mappingId]?.save === editor.save
      ? current : { ...current, [mappingId]: editor });
  }, []);
  const clearMappingEditor = useCallback((mappingId: string) => {
    setEditorsByMappingId((current) => {
      if (!(mappingId in current)) return current;
      const next = { ...current };
      delete next[mappingId];
      return next;
    });
  }, []);
  const setMappingDraft = useCallback((mappingId: string, values: AdapterMappingDraft) => {
    setDraftsByMappingId((current) => {
      if (JSON.stringify(current[mappingId]) === JSON.stringify(values)) return current;
      return { ...current, [mappingId]: values };
    });
  }, []);
  const clearMappingDraft = useCallback((mappingId: string) => {
    setDraftsByMappingId((current) => {
      if (!(mappingId in current)) return current;
      const next = { ...current };
      delete next[mappingId];
      return next;
    });
  }, []);
  const patchMappingDraft = useCallback((mappingId: string, values: AdapterMappingDraft) => {
    setDraftsByMappingId((current) => ({
      ...current,
      [mappingId]: { ...current[mappingId], ...values },
    }));
    setPatchesByMappingId((current) => ({
      ...current,
      [mappingId]: { values, revision: (current[mappingId]?.revision ?? 0) + 1 },
    }));
  }, []);
  const clearMappingPatch = useCallback((mappingId: string, revision: number) => {
    setPatchesByMappingId((current) => {
      if (current[mappingId]?.revision !== revision) return current;
      const next = { ...current };
      delete next[mappingId];
      return next;
    });
  }, []);
  const setMappingPreview = useCallback((mappingId: string, fileName: string, sourceCsv: string) => {
    setPreviewsByMappingId((current) => ({
      ...current,
      [mappingId]: {
        fileName,
        sourceCsv,
        revision: (current[mappingId]?.revision ?? 0) + 1,
      },
    }));
  }, []);
  const value = useMemo(
    () => ({ clearMappingDraft, clearMappingPatch, clearMappingEditor, draftsByMappingId, editorsByMappingId, patchesByMappingId, patchMappingDraft, previewsByMappingId, setMappingDraft, setMappingEditor, setMappingPreview }),
    [clearMappingDraft, clearMappingPatch, clearMappingEditor, draftsByMappingId, editorsByMappingId, patchesByMappingId, patchMappingDraft, previewsByMappingId, setMappingDraft, setMappingEditor, setMappingPreview],
  );

  return <AdapterMappingPreviewContext.Provider value={value}>{children}</AdapterMappingPreviewContext.Provider>;
};

export const useOptionalAdapterMappingPreview = (): AdapterMappingPreviewContextValue | undefined =>
  useContext(AdapterMappingPreviewContext);

export const AdapterMappingDraftPublisher = ({
  mappingId,
  values,
  applyPatch,
  editor,
}: {
  mappingId?: string;
  values: AdapterMappingDraft;
  applyPatch?: (values: AdapterMappingDraft) => void;
  editor?: AdapterMappingEditorState;
}) => {
  const mappingState = useOptionalAdapterMappingPreview();
  const clearMappingDraft = mappingState?.clearMappingDraft;
  const setMappingDraft = mappingState?.setMappingDraft;
  const clearMappingPatch = mappingState?.clearMappingPatch;
  const setMappingEditor = mappingState?.setMappingEditor;
  const clearMappingEditor = mappingState?.clearMappingEditor;
  const patch = mappingId ? mappingState?.patchesByMappingId[mappingId] : undefined;
  const serializedValues = JSON.stringify(values);
  const patchApplied = Boolean(patch && Object.entries(patch.values).every(
    ([key, value]) => JSON.stringify(values[key]) === JSON.stringify(value),
  ));

  useEffect(() => {
    if (!patch || patchApplied) return;
    applyPatch?.(patch.values);
  }, [applyPatch, patch, patchApplied]);

  useEffect(() => {
    if (!mappingId) return;
    if (patch && !patchApplied) return;
    setMappingDraft?.(mappingId, values);
    if (patch) clearMappingPatch?.(mappingId, patch.revision);
  }, [clearMappingPatch, mappingId, patch, patchApplied, serializedValues, setMappingDraft, values]);

  useEffect(() => {
    if (mappingId && editor) setMappingEditor?.(mappingId, editor);
  }, [mappingId, editor?.dirty, editor?.saving, editor?.save, setMappingEditor]);

  useEffect(
    () => () => {
      if (mappingId) clearMappingDraft?.(mappingId);
      if (mappingId) clearMappingEditor?.(mappingId);
    },
    [clearMappingDraft, clearMappingEditor, mappingId],
  );

  return null;
};
